import { type NotificationType, NotificationTypeEnum } from '@invoice/shared/schemas';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, BellRing, Info, Search } from 'lucide-react';
import { useDeferredValue, useState } from 'react';

import { AuthRequiredState } from '@/components/AuthRequiredState';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { summariseNotification } from '@/lib/notification-summary';
import { useCustomerList } from '@/lib/queries/customers';
import {
  useClearNotifications,
  useDeleteNotification,
  useNotifications,
} from '@/lib/queries/notifications';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
});

const FILTERABLE_NOTIFICATION_TYPES = NotificationTypeEnum.options;

function NotificationsPage() {
  const { formatDateTime, t } = useI18n();
  const [searchInput, setSearchInput] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [type, setType] = useState<'' | NotificationType>('');
  const deferredSearch = useDeferredValue(searchInput.trim());
  const query = useNotifications({
    limit: 200,
    customerId: customerId || undefined,
    search: deferredSearch || undefined,
    type: type || undefined,
  });
  const customersQuery = useCustomerList();
  const deleteMutation = useDeleteNotification();
  const clearMutation = useClearNotifications();

  const isUnauthorized = query.error instanceof ApiError && query.error.status === 401;
  const unauthorizedMessage = query.error instanceof ApiError ? query.error.message : undefined;
  if (isUnauthorized) {
    return unauthorizedMessage ? (
      <AuthRequiredState message={unauthorizedMessage} />
    ) : (
      <AuthRequiredState />
    );
  }

  const actionError = deleteMutation.error ?? clearMutation.error;

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {t('notifications.sectionLabel')}
        </p>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {t('notifications.pageTitle')}
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              {t('notifications.pageDescription')}
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => {
              void clearMutation.mutateAsync();
            }}
            disabled={clearMutation.isPending || query.data?.items.length === 0}
            data-testid="notifications-page-clear"
          >
            {clearMutation.isPending ? t('notifications.clearing') : t('notifications.clearAll')}
          </Button>
        </div>
      </header>

      <div className="grid gap-4 rounded-xl border border-border bg-card p-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-2">
          <Label htmlFor="notifications-search">{t('notifications.searchLabel')}</Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="notifications-search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('notifications.searchPlaceholder')}
              data-testid="notifications-search"
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="notifications-tenant-filter">
            {t('notifications.tenantFilterLabel')}
          </Label>
          <Select
            id="notifications-tenant-filter"
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
            disabled={customersQuery.isPending || customersQuery.isError}
            data-testid="notifications-tenant-filter"
          >
            <option value="">{t('notifications.tenantFilter.all')}</option>
            {(customersQuery.data?.items ?? []).map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="notifications-type-filter">{t('notifications.typeFilterLabel')}</Label>
          <Select
            id="notifications-type-filter"
            value={type}
            onChange={(event) => setType(event.target.value as '' | NotificationType)}
            data-testid="notifications-type-filter"
          >
            <option value="">{t('notifications.typeFilter.all')}</option>
            {FILTERABLE_NOTIFICATION_TYPES.map((entry) => (
              <option key={entry} value={entry}>
                {notificationTypeLabel(entry, t)}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {actionError ? (
        <output
          className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="notifications-feedback"
        >
          {actionError.message}
        </output>
      ) : null}

      <div className="rounded-xl border border-border bg-card">
        {query.isPending ? (
          <div className="p-6 text-sm text-muted-foreground">{t('notifications.loading')}</div>
        ) : query.isError ? (
          <div className="p-6 text-sm text-destructive">{query.error.message}</div>
        ) : query.data.items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <BellRing className="size-8 text-muted-foreground" aria-hidden />
            <p className="text-sm font-medium text-foreground">{t('notifications.emptyTitle')}</p>
            <p className="max-w-sm text-xs text-muted-foreground">
              {t('notifications.emptyDescription')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {query.data.items.map((item) => {
              const summary = summariseNotification(item, t);
              const isDeleting =
                deleteMutation.isPending && deleteMutation.variables?.id === item.id;

              return (
                <article
                  key={item.id}
                  className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-start sm:justify-between"
                  data-testid={`notification-row-${item.id}`}
                >
                  <div className="flex min-w-0 flex-1 gap-3">
                    <span className="mt-0.5 shrink-0" aria-hidden>
                      {summary.level === 'error' ? (
                        <AlertTriangle className="size-5 text-destructive" />
                      ) : summary.level === 'warning' ? (
                        <AlertTriangle className="size-5 text-amber-600" />
                      ) : (
                        <Info className="size-5 text-sky-600" />
                      )}
                    </span>

                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{summary.title}</p>
                        <Badge
                          variant={
                            summary.level === 'error'
                              ? 'destructive'
                              : summary.level === 'warning'
                                ? 'warning'
                                : 'info'
                          }
                        >
                          {summary.level === 'error'
                            ? t('notifications.level.error')
                            : summary.level === 'warning'
                              ? t('notifications.level.warning')
                              : t('notifications.level.info')}
                        </Badge>
                        <Badge variant={item.read ? 'muted' : 'outline'}>
                          {item.read ? t('notifications.read') : t('notifications.unread')}
                        </Badge>
                        {item.customerName ? (
                          <Badge variant="outline">{item.customerName}</Badge>
                        ) : null}
                      </div>

                      <p className="text-sm text-muted-foreground">{summary.message}</p>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span>{formatDateTime(item.createdAt)}</span>
                        {summary.meta ? <span>{summary.meta}</span> : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {summary.invoiceId ? (
                      <Link
                        to="/invoices/$id"
                        params={{ id: summary.invoiceId }}
                        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
                      >
                        {t('notifications.openInvoice')}
                      </Link>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void deleteMutation.mutateAsync({ id: item.id });
                      }}
                      disabled={isDeleting}
                      data-testid={`notification-delete-${item.id}`}
                    >
                      {isDeleting ? t('notifications.deleting') : t('notifications.delete')}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function notificationTypeLabel(type: NotificationType, t: ReturnType<typeof useI18n>['t']): string {
  switch (type) {
    case 'invoice_processed':
      return t('notifications.typeFilter.invoice_processed');
    case 'invoice_failed':
      return t('notifications.typeFilter.invoice_failed');
    case 'invoice_manual_review':
      return t('notifications.typeFilter.invoice_manual_review');
    case 'invoice_comment_created':
      return t('notifications.typeFilter.invoice_comment_created');
    case 'invoice_updated':
      return t('notifications.typeFilter.invoice_updated');
    case 'invoice_status_changed':
      return t('notifications.typeFilter.invoice_status_changed');
    case 'invoice_skonto_reminder':
      return t('notifications.typeFilter.invoice_skonto_reminder');
    case 'rule_applied':
      return t('notifications.typeFilter.rule_applied');
    case 'system':
      return t('notifications.typeFilter.system');
    default:
      return type;
  }
}
